// =============================================================
// VARIANCE — The Executive Command Center
// File      : Projects.tsx
// Location  : /home/Applications/variance/web/src/pages/Projects.tsx
// Owner     : VARIANCE frontend application only
// Purpose   : Project Master (Identity, Client, JV, Partners).
// =============================================================

import { useState, useEffect } from 'react';
import { Plus, FolderKanban, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface Company { id: string; name: string; }
interface Project {
  id: string;
  code: string;
  name: string;
  location: string | null;
  company_name: string;
  client_name: string | null;
  jv_partners: string | null;
  financial_partners: string | null;
}

export function Projects() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [formData, setFormData] = useState({
    company_id: '',
    code: '',
    name: '',
    location: '',
    client_name: '',
    jv_partners: '',
    financial_partners: ''
  });
  const [formError, setFormError] = useState('');

  const fetchProjects = () => {
    setIsLoading(true);
    fetch('/api/projects')
      .then(res => res.json())
      .then(data => { if (!data.error) setProjects(data); setIsLoading(false); })
      .catch(() => setIsLoading(false));
  };

  const fetchCompanies = () => {
    fetch('/api/companies')
      .then(res => res.json())
      .then(data => {
        if (!data.error && data.length > 0) {
          setCompanies(data);
          setFormData(prev => ({ ...prev, company_id: data[0].id }));
        }
      });
  };

  useEffect(() => {
    fetchProjects();
    fetchCompanies();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    try {
      const res = await fetch('/api/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create project');
      
      setIsModalOpen(false);
      setFormData({ company_id: companies[0]?.id || '', code: '', name: '', location: '', client_name: '', jv_partners: '', financial_partners: '' });
      fetchProjects();
    } catch (err: any) {
      setFormError(err.message);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Projects</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Manage project identities, clients, and partners.</p>
        </div>
        <button onClick={() => setIsModalOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white hover:opacity-90" style={{ backgroundColor: 'var(--brand-accent)' }}>
          <Plus size={18} /> Add Project
        </button>
      </div>

      <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border-subtle)' }}>
        <table className="w-full text-left border-collapse">
          <thead>
            <tr style={{ backgroundColor: 'var(--bg-panel-alt)' }}>
              <th className="p-4 text-xs font-semibold uppercase text-gray-400">Code</th>
              <th className="p-4 text-xs font-semibold uppercase text-gray-400">Project Name</th>
              <th className="p-4 text-xs font-semibold uppercase text-gray-400">Company</th>
              <th className="p-4 text-xs font-semibold uppercase text-gray-400">Location</th>
              <th className="p-4 text-xs font-semibold uppercase text-gray-400 text-center">View</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={5} className="p-8 text-center text-gray-400">Loading...</td></tr>
            ) : projects.length === 0 ? (
              <tr><td colSpan={5} className="p-8 text-center text-gray-400">No projects found.</td></tr>
            ) : (
              projects.map(p => (
                <tr key={p.id} className="border-t hover:bg-[var(--bg-panel-alt)] transition-colors" style={{ borderColor: 'var(--border-subtle)' }}>
                  <td className="p-4 font-mono text-sm" style={{ color: 'var(--brand-accent)' }}>{p.code}</td>
                  <td className="p-4 font-medium" style={{ color: 'var(--text-primary)' }}>{p.name}</td>
                  <td className="p-4 text-sm" style={{ color: 'var(--text-secondary)' }}>{p.company_name}</td>
                  <td className="p-4 text-sm" style={{ color: 'var(--text-secondary)' }}>{p.location || '-'}</td>
                  <td className="p-4 text-center">
                    <button onClick={() => navigate(`/projects/${p.id}`)} className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-[var(--brand-accent)] transition-colors">
                      <FileText size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm overflow-y-auto py-10">
          <div className="w-full max-w-md p-6 rounded-xl shadow-2xl" style={{ backgroundColor: 'var(--bg-panel)', border: '1px solid var(--border-subtle)' }}>
            <div className="flex items-center gap-3 mb-6">
              <FolderKanban style={{ color: 'var(--brand-accent)' }} />
              <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>Add New Project</h2>
            </div>
            {formError && <div className="p-3 mb-4 rounded text-sm text-red-500 bg-red-500/10">{formError}</div>}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Parent Company</label>
                <select required value={formData.company_id} onChange={e => setFormData({...formData, company_id: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]">
                  <option value="">Select a company...</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Project Code</label>
                  <input required type="text" placeholder="e.g. PROJ-01" value={formData.code} onChange={e => setFormData({...formData, code: e.target.value.toUpperCase()})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]" />
                </div>
                <div>
                  <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Project Name</label>
                  <input required type="text" placeholder="e.g. Tower A" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]" />
                </div>
              </div>

              <div>
                <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Location</label>
                <input type="text" placeholder="e.g. Hyderabad" value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]" />
              </div>

              <div>
                <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Client Name</label>
                <input type="text" placeholder="e.g. Govt of Telangana" value={formData.client_name} onChange={e => setFormData({...formData, client_name: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>JV Partners</label>
                  <input type="text" placeholder="e.g. L&T" value={formData.jv_partners} onChange={e => setFormData({...formData, jv_partners: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]" />
                </div>
                <div>
                  <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Financial Partners</label>
                  <input type="text" placeholder="e.g. HDFC Bank" value={formData.financial_partners} onChange={e => setFormData({...formData, financial_partners: e.target.value})} className="w-full p-2 rounded bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)]" />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 py-2 rounded font-medium bg-[var(--bg-panel-alt)] text-[var(--text-primary)]">Cancel</button>
                <button type="submit" className="flex-1 py-2 rounded font-medium text-white" style={{ backgroundColor: 'var(--brand-accent)' }}>Save Project</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
